import { useState, type SubmitEvent } from 'react'
import {
  Mtds,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  AppLayout
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { ToolErrorSummary } from './developer-tools/ToolErrorSummary'
import type { ToolErrors } from './developer-tools/resultsUpload'
import { GeneratedCodeImage } from './developer-tools/GeneratedCodeImage'

type CodeGeneratorFormData = {
  eftakode: string
  individnummer: string
  varenummer: string
  land: string
}

const buildNewSkrottlappCode = (formData: CodeGeneratorFormData) => {
  const formatVersion = '2'
  const eftakode = formData.eftakode.trim()
  const slaktedato = '0485'
  const skrottnummer = '009603'
  const varenummer = formData.varenummer.trim()
  const unparsedDataBeforeLeieslakt = '0544810020999202012041421'
  const leieslakt = '0000'
  const foedeland = formData.land.trim()
  const individnummer = formData.individnummer.trim()
  const alder = '1122'
  const rase = '22'
  const srm = '0'

  return [
    formatVersion,
    eftakode,
    slaktedato,
    skrottnummer,
    varenummer,
    unparsedDataBeforeLeieslakt,
    leieslakt,
    foedeland,
    individnummer,
    alder,
    rase,
    srm
  ].join('')
}

const buildOldSkrottlappCode = (formData: CodeGeneratorFormData) => {
  const formatVersion = '4'
  const foedeland = formData.land.trim()
  const individnummer = formData.individnummer.trim()
  const alder = '1122'
  const rase = '22'
  const unused = '000'
  const srm = '0'

  return [
    formatVersion,
    foedeland,
    individnummer,
    alder,
    rase,
    unused,
    srm
  ].join('')
}

const codeGeneratorSchema = {
  type: 'object',
  properties: {
    eftakode: {
      type: 'string',
      title: 'EFTA-nummer',
      description: 'Fyll inn EFTA-nummeret for slakteriet.',
      minLength: 4,
      maxLength: 4,
      default: '0134'
    },
    individnummer: {
      type: 'string',
      title: 'Individnummer',
      description: 'Fyll inn individnummeret på 12 siffer.',
      minLength: 12,
      maxLength: 12
    },
    varenummer: {
      type: 'string',
      title: 'Varenummer',
      default: '160080',
      oneOf: [
        { const: '160080', title: 'Storfe' },
        { const: '170080', title: 'Svin' },
        { const: '180080', title: 'Småfe' },
        { const: '190080', title: 'Rein' },
        { const: '188080', title: 'Hest' }
      ]
    },
    land: {
      type: 'string',
      title: 'Opprinnelsesland',
      default: '047',
      oneOf: [
        { const: '001', title: 'USA' },
        { const: '002', title: 'Canada' },
        { const: '007', title: 'Russland' },
        { const: '030', title: 'Hellas' },
        { const: '031', title: 'Nederland' },
        { const: '032', title: 'Belgia' },
        { const: '033', title: 'Frankriket' },
        { const: '034', title: 'Spania' },
        { const: '036', title: 'Ungarn' },
        { const: '039', title: 'Italia' },
        { const: '040', title: 'Romania' },
        { const: '041', title: 'Sveits' },
        { const: '043', title: 'Østerrike' },
        { const: '044', title: 'Storbrittania' },
        { const: '045', title: 'Danmark' },
        { const: '046', title: 'Sverige' },
        { const: '047', title: 'Norge' },
        { const: '048', title: 'Polen' },
        { const: '049', title: 'Tyskland' },
        { const: '052', title: 'Mexico' },
        { const: '054', title: 'Argentina' },
        { const: '055', title: 'Brasil' },
        { const: '061', title: 'Australia' },
        { const: '064', title: 'New Zealand' },
        { const: '081', title: 'Japan' },
        { const: '090', title: 'Tyrkia' },
        { const: '298', title: 'Færøyene' },
        { const: '299', title: 'Grønland' },
        { const: '351', title: 'Portugal' },
        { const: '352', title: 'Luxenburg' },
        { const: '353', title: 'Irland' },
        { const: '354', title: 'Island' },
        { const: '355', title: 'Albania' }
      ]
    }
  },
  required: ['eftakode', 'individnummer', 'varenummer', 'land']
} as const

export const CodeGeneratorScreen = () => {
  const [formData, setFormData] = useState<CodeGeneratorFormData>({
    eftakode: '0134',
    individnummer: '',
    varenummer: '160080',
    land: '047'
  })
  const [errors, setErrors] = useState<ToolErrors>({})
  const [output, setOutput] = useState<{
    dataMatrix: string
    barcode: string
    draft: string
    generation: number
  }>()
  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    const next: ToolErrors = {}
    if (!/^\d{4}$/.test(formData.eftakode.trim()))
      next['code-eftakode'] = 'EFTA-nummer må bestå av 4 siffer.'
    if (!/^\d{12}$/.test(formData.individnummer.trim()))
      next['code-individnummer'] = 'Individnummer må bestå av 12 siffer.'
    if (
      !codeGeneratorSchema.properties.varenummer.oneOf.some(
        (item) => item.const === formData.varenummer
      )
    )
      next['code-varenummer'] = 'Velg et varenummer fra listen.'
    if (
      !codeGeneratorSchema.properties.land.oneOf.some(
        (item) => item.const === formData.land
      )
    )
      next['code-land'] = 'Velg opprinnelsesland fra listen.'
    setErrors(next)
    if (!Object.keys(next).length) {
      setOutput({
        dataMatrix: buildNewSkrottlappCode(formData),
        barcode: buildOldSkrottlappCode(formData),
        draft: JSON.stringify(formData),
        generation: (output?.generation ?? 0) + 1
      })
    }
  }
  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} title={'Kodegenerator'} />}
      useMobileBar={false}
      isFullWidth
    >
      <ScreenLayout
        header={
          <ScreenHeader
            title='Kodegenerator'
            description={
              <p>
                Utviklerverktøy for skrottlapper. Genererer DataMatrix og
                Code128 med faste testverdier for øvrige kodefelt.
              </p>
            }
          />
        }
      >
        <Mtds.Grid data-size='sm' data-gap='4'>
          <Mtds.Card as={Mtds.Grid} data-pad='4'>
            <Mtds.Heading as='h2' data-size='sm'>
              Kodegrunnlag
            </Mtds.Heading>
            <Mtds.Alert data-color='info'>
              Ved generering sendes kodeinnholdet til den eksterne
              bildetjenesten barcode.tec-it.com. Bruk testdata, ikke
              personopplysninger. Ingen data lagres i Prøvetaking.
            </Mtds.Alert>
            <Mtds.Grid as='form' noValidate onSubmit={onSubmit}>
              <ToolErrorSummary errors={errors} />
              <Mtds.Grid data-items='250'>
                {(
                  Object.keys(
                    codeGeneratorSchema.properties
                  ) as (keyof CodeGeneratorFormData)[]
                ).map((key) => {
                  const definition = codeGeneratorSchema.properties[key]
                  return 'oneOf' in definition ? (
                    <Mtds.Field
                      key={key}
                      as='select'
                      id={`code-${key}`}
                      label={definition.title}
                      required
                      value={formData[key]}
                      validation={errors[`code-${key}`]}
                      options={definition.oneOf.map((item) => ({
                        value: item.const,
                        label: `${item.title} (${item.const})`
                      }))}
                      onChange={(event) =>
                        setFormData({ ...formData, [key]: event.target.value })
                      }
                    />
                  ) : (
                    <Mtds.Field
                      key={key}
                      as='input'
                      id={`code-${key}`}
                      label={definition.title}
                      description={definition.description}
                      required
                      inputMode='numeric'
                      value={formData[key]}
                      validation={errors[`code-${key}`]}
                      onChange={(event) =>
                        setFormData({ ...formData, [key]: event.target.value })
                      }
                    />
                  )
                })}
              </Mtds.Grid>
              <Mtds.Flex>
                <Mtds.Button data-variant='primary' type='submit'>
                  Generer koder
                </Mtds.Button>
              </Mtds.Flex>
            </Mtds.Grid>
          </Mtds.Card>
          <Mtds.Group as={Mtds.Grid} data-pad='4'>
            <Mtds.Heading as='h2' data-size='sm'>
              Genererte koder
            </Mtds.Heading>
            {!output && (
              <Mtds.Muted>
                Fyll ut kodegrunnlaget og velg «Generer koder». Bildene hentes
                først da.
              </Mtds.Muted>
            )}
            {output && (
              <>
                {output.draft !== JSON.stringify(formData) && (
                  <Mtds.Alert data-color='warning'>
                    Kodegrunnlaget er endret. Kodene nedenfor viser forrige
                    generering.
                  </Mtds.Alert>
                )}
                <Mtds.Grid
                  data-items='350'
                  data-align='start'
                  key={output.generation}
                >
                  <GeneratedCodeImage
                    id='dataMatrixImage'
                    title='DataMatrix'
                    code={output.dataMatrix}
                    url={`https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(output.dataMatrix)}&code=DataMatrix&unit=Mm&dmsize=Default&modulewidth=2`}
                  />
                  <GeneratedCodeImage
                    id='barcodeImage'
                    title='Strekkode (Code128)'
                    code={output.barcode}
                    url={`https://barcode.tec-it.com/barcode.ashx?data=${encodeURIComponent(output.barcode)}&code=Code128&translate-esc=on`}
                  />
                </Mtds.Grid>
              </>
            )}
          </Mtds.Group>
        </Mtds.Grid>
      </ScreenLayout>
    </AppLayout>
  )
}
